export interface KeywordEntry {
  keyword: string;
  aliases?: string[];
}

export interface KeywordCategory {
  label: string;
  variant: 'blue' | 'green' | 'purple' | 'orange' | 'gray';
  keywords: KeywordEntry[];
}

export type CategoryKey =
  | 'hard_skills'
  | 'soft_skills'
  | 'certifications'
  | 'tools_technologies'
  | 'industry_terms';

export const keywordDictionary: Record<CategoryKey, KeywordCategory> = {
  hard_skills: {
    label: 'Hard Skills',
    variant: 'blue',
    keywords: [
      // Programming & Development
      { keyword: 'JavaScript', aliases: ['JS'] },
      { keyword: 'TypeScript', aliases: ['TS'] },
      { keyword: 'Python' },
      { keyword: 'Java' },
      { keyword: 'C++', aliases: ['CPP'] },
      { keyword: 'C#', aliases: ['C Sharp', 'CSharp'] },
      { keyword: 'Ruby' },
      { keyword: 'Go', aliases: ['Golang'] },
      { keyword: 'Rust' },
      { keyword: 'Swift' },
      { keyword: 'Kotlin' },
      { keyword: 'PHP' },
      { keyword: 'Scala' },
      { keyword: 'R' },
      { keyword: 'MATLAB' },
      { keyword: 'SQL' },
      { keyword: 'NoSQL' },
      { keyword: 'GraphQL' },
      { keyword: 'HTML', aliases: ['HTML5'] },
      { keyword: 'CSS', aliases: ['CSS3'] },
      { keyword: 'REST APIs', aliases: ['RESTful', 'REST API', 'RESTful APIs'] },
      { keyword: 'Microservices' },
      { keyword: 'Object-Oriented Programming', aliases: ['OOP'] },
      { keyword: 'Functional Programming' },
      { keyword: 'Test-Driven Development', aliases: ['TDD'] },
      { keyword: 'CI/CD', aliases: ['Continuous Integration', 'Continuous Deployment'] },
      { keyword: 'DevOps' },
      { keyword: 'Agile', aliases: ['Agile Methodology'] },
      { keyword: 'Scrum' },
      { keyword: 'Kanban' },
      { keyword: 'Version Control', aliases: ['Git'] },
      { keyword: 'Unit Testing' },
      { keyword: 'Integration Testing' },
      { keyword: 'API Development' },
      { keyword: 'Full-Stack Development', aliases: ['Full Stack'] },
      { keyword: 'Frontend Development', aliases: ['Front-End', 'Front End'] },
      { keyword: 'Backend Development', aliases: ['Back-End', 'Back End'] },
      { keyword: 'Mobile Development' },
      { keyword: 'Cloud Computing' },
      { keyword: 'Machine Learning', aliases: ['ML'] },
      { keyword: 'Deep Learning' },
      { keyword: 'Natural Language Processing', aliases: ['NLP'] },
      { keyword: 'Computer Vision' },
      { keyword: 'Artificial Intelligence', aliases: ['AI'] },
      { keyword: 'Data Science' },
      { keyword: 'Data Engineering' },
      { keyword: 'Data Analysis' },
      { keyword: 'Data Visualization' },
      { keyword: 'Data Modeling' },
      { keyword: 'ETL' },
      { keyword: 'Data Warehousing' },
      { keyword: 'Big Data' },
      { keyword: 'Statistical Analysis' },
      { keyword: 'A/B Testing' },
      { keyword: 'Cybersecurity', aliases: ['Information Security', 'InfoSec'] },
      { keyword: 'Penetration Testing', aliases: ['Pen Testing'] },
      { keyword: 'Network Security' },
      { keyword: 'Cloud Security' },
      { keyword: 'Encryption' },
      { keyword: 'Blockchain' },
      { keyword: 'System Design' },
      { keyword: 'Database Design' },
      { keyword: 'Infrastructure as Code', aliases: ['IaC'] },
      // Data & Analytics
      { keyword: 'Business Intelligence', aliases: ['BI'] },
      { keyword: 'Predictive Analytics' },
      { keyword: 'Regression Analysis' },
      { keyword: 'Forecasting' },
      { keyword: 'KPI Development' },
      { keyword: 'Dashboard Design' },
      { keyword: 'Report Generation' },
      // Finance & Accounting
      { keyword: 'Financial Modeling' },
      { keyword: 'Financial Analysis' },
      { keyword: 'Budgeting' },
      { keyword: 'Forecasting' },
      { keyword: 'Revenue Recognition' },
      { keyword: 'Accounts Payable', aliases: ['AP'] },
      { keyword: 'Accounts Receivable', aliases: ['AR'] },
      { keyword: 'General Ledger', aliases: ['GL'] },
      { keyword: 'Tax Preparation' },
      { keyword: 'Auditing' },
      { keyword: 'Bookkeeping' },
      { keyword: 'Payroll' },
      { keyword: 'Cost Accounting' },
      { keyword: 'GAAP' },
      { keyword: 'IFRS' },
      { keyword: 'Financial Reporting' },
      { keyword: 'Risk Management' },
      { keyword: 'Investment Analysis' },
      { keyword: 'Portfolio Management' },
      { keyword: 'Mergers and Acquisitions', aliases: ['M&A'] },
      { keyword: 'Due Diligence' },
      { keyword: 'Valuation' },
      { keyword: 'Underwriting' },
      { keyword: 'Credit Analysis' },
      // Marketing & Sales
      { keyword: 'SEO', aliases: ['Search Engine Optimization'] },
      { keyword: 'SEM', aliases: ['Search Engine Marketing'] },
      { keyword: 'PPC', aliases: ['Pay-Per-Click'] },
      { keyword: 'Content Marketing' },
      { keyword: 'Email Marketing' },
      { keyword: 'Social Media Marketing' },
      { keyword: 'Digital Marketing' },
      { keyword: 'Marketing Automation' },
      { keyword: 'Lead Generation' },
      { keyword: 'Conversion Rate Optimization', aliases: ['CRO'] },
      { keyword: 'Copywriting' },
      { keyword: 'Brand Strategy' },
      { keyword: 'Market Research' },
      { keyword: 'Competitive Analysis' },
      { keyword: 'Account Management' },
      { keyword: 'Business Development' },
      { keyword: 'Sales Strategy' },
      { keyword: 'Pipeline Management' },
      { keyword: 'Customer Acquisition' },
      { keyword: 'Demand Generation' },
      { keyword: 'Product Marketing' },
      { keyword: 'Growth Marketing' },
      { keyword: 'Influencer Marketing' },
      { keyword: 'Affiliate Marketing' },
      { keyword: 'Public Relations', aliases: ['PR'] },
      { keyword: 'Media Buying' },
      { keyword: 'Campaign Management' },
      // Healthcare
      { keyword: 'Patient Care' },
      { keyword: 'Clinical Research' },
      { keyword: 'HIPAA Compliance', aliases: ['HIPAA'] },
      { keyword: 'Electronic Health Records', aliases: ['EHR', 'EMR'] },
      { keyword: 'Medical Coding' },
      { keyword: 'Medical Billing' },
      { keyword: 'Pharmacology' },
      { keyword: 'Phlebotomy' },
      { keyword: 'Nursing' },
      { keyword: 'Triage' },
      { keyword: 'Case Management' },
      { keyword: 'Care Coordination' },
      { keyword: 'Vital Signs' },
      { keyword: 'CPR' },
      { keyword: 'BLS', aliases: ['Basic Life Support'] },
      { keyword: 'ACLS', aliases: ['Advanced Cardiac Life Support'] },
      // Project & Product Management
      { keyword: 'Project Management' },
      { keyword: 'Product Management' },
      { keyword: 'Program Management' },
      { keyword: 'Change Management' },
      { keyword: 'Stakeholder Management' },
      { keyword: 'Requirements Gathering' },
      { keyword: 'User Stories' },
      { keyword: 'Roadmapping' },
      { keyword: 'Sprint Planning' },
      { keyword: 'Backlog Management' },
      { keyword: 'Resource Allocation' },
      { keyword: 'Vendor Management' },
      { keyword: 'Process Improvement' },
      { keyword: 'Lean' },
      { keyword: 'Six Sigma' },
      { keyword: 'Supply Chain Management' },
      { keyword: 'Inventory Management' },
      { keyword: 'Logistics' },
      { keyword: 'Procurement' },
      { keyword: 'Quality Assurance', aliases: ['QA'] },
      { keyword: 'Quality Control', aliases: ['QC'] },
      // Design & UX
      { keyword: 'UX Design', aliases: ['User Experience Design'] },
      { keyword: 'UI Design', aliases: ['User Interface Design'] },
      { keyword: 'Interaction Design' },
      { keyword: 'Visual Design' },
      { keyword: 'Graphic Design' },
      { keyword: 'Wireframing' },
      { keyword: 'Prototyping' },
      { keyword: 'User Research' },
      { keyword: 'Usability Testing' },
      { keyword: 'Information Architecture' },
      { keyword: 'Design Systems' },
      { keyword: 'Responsive Design' },
      { keyword: 'Accessibility', aliases: ['WCAG', 'ADA Compliance'] },
      // HR & People
      { keyword: 'Talent Acquisition' },
      { keyword: 'Recruiting' },
      { keyword: 'Onboarding' },
      { keyword: 'Employee Relations' },
      { keyword: 'Performance Management' },
      { keyword: 'Compensation and Benefits', aliases: ['Comp & Benefits'] },
      { keyword: 'HRIS' },
      { keyword: 'Workforce Planning' },
      { keyword: 'Training and Development', aliases: ['L&D', 'Learning and Development'] },
      { keyword: 'Diversity and Inclusion', aliases: ['DEI', 'D&I'] },
      // Legal
      { keyword: 'Contract Negotiation' },
      { keyword: 'Legal Research' },
      { keyword: 'Compliance' },
      { keyword: 'Regulatory Affairs' },
      { keyword: 'Intellectual Property', aliases: ['IP'] },
      { keyword: 'Litigation' },
      { keyword: 'Corporate Governance' },
      // Writing & Communication
      { keyword: 'Technical Writing' },
      { keyword: 'Grant Writing' },
      { keyword: 'Proposal Writing' },
      { keyword: 'Editing' },
      { keyword: 'Proofreading' },
      { keyword: 'Content Strategy' },
      { keyword: 'Storytelling' },
    ],
  },

  soft_skills: {
    label: 'Soft Skills',
    variant: 'green',
    keywords: [
      { keyword: 'Leadership' },
      { keyword: 'Communication' },
      { keyword: 'Teamwork', aliases: ['Team Player', 'Collaboration'] },
      { keyword: 'Problem Solving', aliases: ['Problem-Solving'] },
      { keyword: 'Critical Thinking' },
      { keyword: 'Analytical Thinking' },
      { keyword: 'Strategic Thinking' },
      { keyword: 'Decision Making', aliases: ['Decision-Making'] },
      { keyword: 'Time Management' },
      { keyword: 'Organization', aliases: ['Organizational Skills'] },
      { keyword: 'Attention to Detail' },
      { keyword: 'Adaptability' },
      { keyword: 'Creativity' },
      { keyword: 'Innovation' },
      { keyword: 'Negotiation' },
      { keyword: 'Conflict Resolution' },
      { keyword: 'Mentoring', aliases: ['Coaching'] },
      { keyword: 'Presentation Skills' },
      { keyword: 'Public Speaking' },
      { keyword: 'Written Communication' },
      { keyword: 'Verbal Communication' },
      { keyword: 'Interpersonal Skills' },
      { keyword: 'Customer Service' },
      { keyword: 'Client Relations' },
      { keyword: 'Relationship Building' },
      { keyword: 'Cross-Functional Collaboration', aliases: ['Cross-Functional'] },
      { keyword: 'Self-Motivated', aliases: ['Self-Starter', 'Self Starter'] },
      { keyword: 'Work Ethic' },
      { keyword: 'Accountability' },
      { keyword: 'Emotional Intelligence', aliases: ['EQ'] },
      { keyword: 'Empathy' },
      { keyword: 'Active Listening' },
      { keyword: 'Multitasking' },
      { keyword: 'Prioritization' },
      { keyword: 'Flexibility' },
      { keyword: 'Resilience' },
      { keyword: 'Initiative' },
      { keyword: 'Detail-Oriented' },
      { keyword: 'Results-Driven', aliases: ['Results-Oriented', 'Results Oriented'] },
      { keyword: 'Goal-Oriented' },
      { keyword: 'Fast Learner', aliases: ['Quick Learner'] },
      { keyword: 'Resourcefulness' },
      { keyword: 'Persuasion' },
      { keyword: 'Influence' },
      { keyword: 'Cultural Awareness' },
      { keyword: 'Trustworthiness' },
    ],
  },

  certifications: {
    label: 'Certifications',
    variant: 'purple',
    keywords: [
      // Tech & Cloud
      { keyword: 'AWS Certified', aliases: ['AWS Certification', 'Amazon Web Services Certified'] },
      { keyword: 'AWS Solutions Architect' },
      { keyword: 'AWS Developer Associate' },
      { keyword: 'AWS SysOps Administrator' },
      { keyword: 'Azure Certified', aliases: ['Microsoft Azure Certified'] },
      { keyword: 'Azure Solutions Architect' },
      { keyword: 'Azure Administrator' },
      { keyword: 'Google Cloud Certified', aliases: ['GCP Certified'] },
      { keyword: 'Google Cloud Professional' },
      { keyword: 'CompTIA A+', aliases: ['A+'] },
      { keyword: 'CompTIA Network+', aliases: ['Network+'] },
      { keyword: 'CompTIA Security+', aliases: ['Security+'] },
      { keyword: 'CISSP', aliases: ['Certified Information Systems Security Professional'] },
      { keyword: 'CISM', aliases: ['Certified Information Security Manager'] },
      { keyword: 'CEH', aliases: ['Certified Ethical Hacker'] },
      { keyword: 'CCNA', aliases: ['Cisco Certified Network Associate'] },
      { keyword: 'CCNP', aliases: ['Cisco Certified Network Professional'] },
      { keyword: 'ITIL', aliases: ['ITIL Certified', 'ITIL Foundation'] },
      { keyword: 'TOGAF' },
      // Project Management
      { keyword: 'PMP', aliases: ['Project Management Professional'] },
      { keyword: 'CAPM', aliases: ['Certified Associate in Project Management'] },
      { keyword: 'PRINCE2' },
      { keyword: 'CSM', aliases: ['Certified ScrumMaster', 'Certified Scrum Master'] },
      { keyword: 'SAFe', aliases: ['SAFe Certified', 'Scaled Agile Framework'] },
      { keyword: 'PMI-ACP', aliases: ['Agile Certified Practitioner'] },
      // Finance & Accounting
      { keyword: 'CPA', aliases: ['Certified Public Accountant'] },
      { keyword: 'CFA', aliases: ['Chartered Financial Analyst'] },
      { keyword: 'CMA', aliases: ['Certified Management Accountant'] },
      { keyword: 'CFP', aliases: ['Certified Financial Planner'] },
      { keyword: 'FRM', aliases: ['Financial Risk Manager'] },
      { keyword: 'Series 7' },
      { keyword: 'Series 63' },
      { keyword: 'Series 66' },
      // Healthcare
      { keyword: 'RN', aliases: ['Registered Nurse'] },
      { keyword: 'BSN', aliases: ['Bachelor of Science in Nursing'] },
      { keyword: 'NP', aliases: ['Nurse Practitioner'] },
      { keyword: 'CNA', aliases: ['Certified Nursing Assistant'] },
      { keyword: 'BCLS' },
      { keyword: 'CPC', aliases: ['Certified Professional Coder'] },
      // HR
      { keyword: 'PHR', aliases: ['Professional in Human Resources'] },
      { keyword: 'SPHR', aliases: ['Senior Professional in Human Resources'] },
      { keyword: 'SHRM-CP' },
      { keyword: 'SHRM-SCP' },
      // Six Sigma & Quality
      { keyword: 'Six Sigma Green Belt', aliases: ['Green Belt'] },
      { keyword: 'Six Sigma Black Belt', aliases: ['Black Belt'] },
      { keyword: 'Lean Six Sigma' },
      // Data & Analytics
      { keyword: 'Google Analytics Certified', aliases: ['GA Certified', 'Google Analytics Certification'] },
      { keyword: 'Tableau Certified' },
      { keyword: 'Salesforce Certified', aliases: ['Salesforce Certification'] },
      { keyword: 'Salesforce Administrator' },
      { keyword: 'HubSpot Certified', aliases: ['HubSpot Certification'] },
      // Other
      { keyword: 'Six Sigma' },
      { keyword: 'Certified ScrumMaster' },
      { keyword: 'LEED', aliases: ['LEED Certified'] },
      { keyword: 'PMP Certified' },
    ],
  },

  tools_technologies: {
    label: 'Tools & Technologies',
    variant: 'orange',
    keywords: [
      // Frameworks & Libraries
      { keyword: 'React', aliases: ['React.js', 'ReactJS'] },
      { keyword: 'Next.js', aliases: ['NextJS'] },
      { keyword: 'Angular', aliases: ['AngularJS'] },
      { keyword: 'Vue.js', aliases: ['Vue', 'VueJS'] },
      { keyword: 'Svelte' },
      { keyword: 'Node.js', aliases: ['NodeJS', 'Node'] },
      { keyword: 'Express', aliases: ['Express.js'] },
      { keyword: 'Django' },
      { keyword: 'Flask' },
      { keyword: 'FastAPI' },
      { keyword: 'Spring Boot', aliases: ['Spring'] },
      { keyword: 'Ruby on Rails', aliases: ['Rails'] },
      { keyword: 'Laravel' },
      { keyword: '.NET', aliases: ['dotnet', 'ASP.NET'] },
      { keyword: 'Flutter' },
      { keyword: 'React Native' },
      { keyword: 'Tailwind CSS', aliases: ['TailwindCSS', 'Tailwind'] },
      { keyword: 'Bootstrap' },
      { keyword: 'jQuery' },
      { keyword: 'Redux' },
      { keyword: 'Webpack' },
      { keyword: 'Vite' },
      // Cloud & Infrastructure
      { keyword: 'AWS', aliases: ['Amazon Web Services'] },
      { keyword: 'Azure', aliases: ['Microsoft Azure'] },
      { keyword: 'Google Cloud Platform', aliases: ['GCP'] },
      { keyword: 'Heroku' },
      { keyword: 'Vercel' },
      { keyword: 'Netlify' },
      { keyword: 'DigitalOcean' },
      { keyword: 'Docker' },
      { keyword: 'Kubernetes', aliases: ['K8s'] },
      { keyword: 'Terraform' },
      { keyword: 'Ansible' },
      { keyword: 'Jenkins' },
      { keyword: 'GitHub Actions' },
      { keyword: 'GitLab CI' },
      { keyword: 'CircleCI' },
      { keyword: 'Travis CI' },
      { keyword: 'ArgoCD' },
      { keyword: 'Helm' },
      { keyword: 'Nginx' },
      { keyword: 'Apache' },
      { keyword: 'Linux' },
      { keyword: 'AWS Lambda', aliases: ['Lambda'] },
      { keyword: 'S3', aliases: ['AWS S3'] },
      { keyword: 'EC2', aliases: ['AWS EC2'] },
      { keyword: 'CloudFormation' },
      // Databases
      { keyword: 'PostgreSQL', aliases: ['Postgres'] },
      { keyword: 'MySQL' },
      { keyword: 'MongoDB' },
      { keyword: 'Redis' },
      { keyword: 'Elasticsearch' },
      { keyword: 'DynamoDB' },
      { keyword: 'Cassandra' },
      { keyword: 'Oracle Database', aliases: ['Oracle DB'] },
      { keyword: 'SQL Server', aliases: ['Microsoft SQL Server', 'MSSQL'] },
      { keyword: 'SQLite' },
      { keyword: 'Firebase' },
      { keyword: 'Supabase' },
      // Data & ML Tools
      { keyword: 'Pandas' },
      { keyword: 'NumPy' },
      { keyword: 'Scikit-learn', aliases: ['sklearn'] },
      { keyword: 'TensorFlow' },
      { keyword: 'PyTorch' },
      { keyword: 'Keras' },
      { keyword: 'Jupyter', aliases: ['Jupyter Notebook'] },
      { keyword: 'Apache Spark', aliases: ['Spark'] },
      { keyword: 'Hadoop' },
      { keyword: 'Kafka', aliases: ['Apache Kafka'] },
      { keyword: 'Airflow', aliases: ['Apache Airflow'] },
      { keyword: 'dbt' },
      { keyword: 'Snowflake' },
      { keyword: 'Databricks' },
      { keyword: 'Redshift' },
      // Design Tools
      { keyword: 'Figma' },
      { keyword: 'Sketch' },
      { keyword: 'Adobe XD' },
      { keyword: 'InVision' },
      { keyword: 'Adobe Photoshop', aliases: ['Photoshop'] },
      { keyword: 'Adobe Illustrator', aliases: ['Illustrator'] },
      { keyword: 'Adobe InDesign', aliases: ['InDesign'] },
      { keyword: 'Adobe Creative Suite', aliases: ['Creative Suite', 'Adobe CC'] },
      { keyword: 'Canva' },
      { keyword: 'Adobe Premiere', aliases: ['Premiere Pro'] },
      { keyword: 'After Effects' },
      // Business & Analytics Tools
      { keyword: 'Tableau' },
      { keyword: 'Power BI' },
      { keyword: 'Looker' },
      { keyword: 'Google Analytics', aliases: ['GA4'] },
      { keyword: 'Google Tag Manager', aliases: ['GTM'] },
      { keyword: 'Google Ads', aliases: ['AdWords'] },
      { keyword: 'Facebook Ads', aliases: ['Meta Ads'] },
      { keyword: 'Mixpanel' },
      { keyword: 'Amplitude' },
      { keyword: 'Segment' },
      // Productivity & PM Tools
      { keyword: 'Jira' },
      { keyword: 'Confluence' },
      { keyword: 'Asana' },
      { keyword: 'Trello' },
      { keyword: 'Monday.com' },
      { keyword: 'Notion' },
      { keyword: 'Slack' },
      { keyword: 'Microsoft Teams', aliases: ['Teams'] },
      { keyword: 'Zoom' },
      { keyword: 'Microsoft Office', aliases: ['MS Office'] },
      { keyword: 'Microsoft Excel', aliases: ['Excel'] },
      { keyword: 'Microsoft Word', aliases: ['Word'] },
      { keyword: 'Microsoft PowerPoint', aliases: ['PowerPoint'] },
      { keyword: 'Google Workspace', aliases: ['G Suite'] },
      { keyword: 'Google Sheets' },
      // CRM & Marketing
      { keyword: 'Salesforce' },
      { keyword: 'HubSpot' },
      { keyword: 'Marketo' },
      { keyword: 'Mailchimp' },
      { keyword: 'Pardot' },
      { keyword: 'Zendesk' },
      { keyword: 'Intercom' },
      { keyword: 'Hootsuite' },
      { keyword: 'Sprout Social' },
      { keyword: 'Buffer' },
      { keyword: 'Ahrefs' },
      { keyword: 'SEMrush' },
      { keyword: 'Moz' },
      { keyword: 'WordPress' },
      { keyword: 'Shopify' },
      // ERP & Finance
      { keyword: 'SAP' },
      { keyword: 'Oracle ERP' },
      { keyword: 'NetSuite' },
      { keyword: 'QuickBooks' },
      { keyword: 'Xero' },
      // Version Control
      { keyword: 'Git' },
      { keyword: 'GitHub' },
      { keyword: 'GitLab' },
      { keyword: 'Bitbucket' },
      // Healthcare Tools
      { keyword: 'Epic' },
      { keyword: 'Cerner' },
      { keyword: 'MEDITECH' },
    ],
  },

  industry_terms: {
    label: 'Industry Terms',
    variant: 'gray',
    keywords: [
      // Business & Strategy
      { keyword: 'ROI', aliases: ['Return on Investment'] },
      { keyword: 'KPI', aliases: ['Key Performance Indicator', 'Key Performance Indicators'] },
      { keyword: 'OKR', aliases: ['Objectives and Key Results'] },
      { keyword: 'SLA', aliases: ['Service Level Agreement'] },
      { keyword: 'SaaS', aliases: ['Software as a Service'] },
      { keyword: 'B2B', aliases: ['Business to Business'] },
      { keyword: 'B2C', aliases: ['Business to Consumer'] },
      { keyword: 'P&L', aliases: ['Profit and Loss'] },
      { keyword: 'EBITDA' },
      { keyword: 'Revenue Growth' },
      { keyword: 'Cost Reduction' },
      { keyword: 'Scalability' },
      { keyword: 'Go-to-Market', aliases: ['GTM Strategy'] },
      { keyword: 'Market Penetration' },
      { keyword: 'Customer Retention' },
      { keyword: 'Churn Rate' },
      { keyword: 'Customer Lifetime Value', aliases: ['CLV', 'LTV'] },
      { keyword: 'Customer Satisfaction', aliases: ['CSAT'] },
      { keyword: 'Net Promoter Score', aliases: ['NPS'] },
      { keyword: 'Total Addressable Market', aliases: ['TAM'] },
      { keyword: 'Product-Market Fit' },
      { keyword: 'Minimum Viable Product', aliases: ['MVP'] },
      { keyword: 'User Engagement' },
      { keyword: 'Conversion Rate' },
      { keyword: 'Funnel Optimization' },
      { keyword: 'Growth Hacking' },
      // Compliance & Regulation
      { keyword: 'SOX Compliance', aliases: ['Sarbanes-Oxley', 'SOX'] },
      { keyword: 'GDPR' },
      { keyword: 'CCPA' },
      { keyword: 'SOC 2', aliases: ['SOC2'] },
      { keyword: 'ISO 27001' },
      { keyword: 'PCI DSS', aliases: ['PCI Compliance'] },
      { keyword: 'FDA Compliance', aliases: ['FDA'] },
      { keyword: 'OSHA' },
      { keyword: 'AML', aliases: ['Anti-Money Laundering'] },
      { keyword: 'KYC', aliases: ['Know Your Customer'] },
      // Tech & Engineering
      { keyword: 'SaaS' },
      { keyword: 'PaaS', aliases: ['Platform as a Service'] },
      { keyword: 'IaaS', aliases: ['Infrastructure as a Service'] },
      { keyword: 'API', aliases: ['Application Programming Interface'] },
      { keyword: 'SDK', aliases: ['Software Development Kit'] },
      { keyword: 'Single Sign-On', aliases: ['SSO'] },
      { keyword: 'OAuth' },
      { keyword: 'Containerization' },
      { keyword: 'Serverless' },
      { keyword: 'Edge Computing' },
      { keyword: 'High Availability', aliases: ['HA'] },
      { keyword: 'Disaster Recovery', aliases: ['DR'] },
      { keyword: 'Load Balancing' },
      { keyword: 'Caching' },
      { keyword: 'Latency' },
      { keyword: 'Throughput' },
      { keyword: 'Uptime' },
      { keyword: 'SRE', aliases: ['Site Reliability Engineering'] },
      { keyword: 'Observability' },
      { keyword: 'Monitoring' },
      { keyword: 'Incident Response' },
      { keyword: 'Technical Debt' },
      { keyword: 'Refactoring' },
      { keyword: 'Code Review' },
      { keyword: 'Pair Programming' },
      // Healthcare Terms
      { keyword: 'ICD-10' },
      { keyword: 'CPT Codes' },
      { keyword: 'Revenue Cycle Management', aliases: ['RCM'] },
      { keyword: 'Population Health' },
      { keyword: 'Telehealth', aliases: ['Telemedicine'] },
      { keyword: 'Value-Based Care' },
      { keyword: 'Patient Engagement' },
      { keyword: 'Clinical Trials' },
      { keyword: 'HL7', aliases: ['Health Level Seven'] },
      { keyword: 'FHIR' },
      // HR & Organizational
      { keyword: 'Employee Engagement' },
      { keyword: 'Succession Planning' },
      { keyword: 'Organizational Development' },
      { keyword: 'Change Management' },
      { keyword: 'Employer Branding' },
      { keyword: 'Retention Rate' },
      { keyword: 'Turnover Rate' },
      { keyword: 'Headcount Planning' },
      // Supply Chain & Operations
      { keyword: 'Just-in-Time', aliases: ['JIT'] },
      { keyword: 'Supply Chain Optimization' },
      { keyword: 'Demand Planning' },
      { keyword: 'Warehouse Management' },
      { keyword: 'Last Mile Delivery' },
      { keyword: 'Fulfillment' },
      { keyword: 'ERP', aliases: ['Enterprise Resource Planning'] },
      { keyword: 'CRM', aliases: ['Customer Relationship Management'] },
      // Education
      { keyword: 'Curriculum Development' },
      { keyword: 'Instructional Design' },
      { keyword: 'E-Learning' },
      { keyword: 'LMS', aliases: ['Learning Management System'] },
      { keyword: 'Student Outcomes' },
      { keyword: 'Assessment Design' },
    ],
  },
};
